// libs/election-api-types/src/election-api-types.ts
import {
  array,
  boolean,
  enum as enum2,
  number,
  object,
  record,
  string
} from "zod";
var partyCodeSchema = enum2([
  "ALP",
  "AJP",
  "GRN",
  "IND",
  "KAP",
  "LIB",
  "LCQP",
  "LDP",
  "LBT",
  "LIB",
  "LNP",
  "ONP",
  "PHON",
  "OTH",
  "ACP",
  "ANTI_SOC",
  "ASP",
  "CLP",
  "CP",
  "DEM",
  "FLP",
  "FSU",
  "FT",
  "FU",
  "IND",
  "IND_LAB",
  "IND_NAT",
  "IND_PROT",
  "LANG_LAB",
  "LCL",
  "LCP",
  "LNQ",
  "LP",
  "NAT",
  "NAT_FARMERS",
  "NCP",
  "NP",
  "NPWA",
  "PROT",
  "SAP",
  "ST_CP",
  "UAP",
  "UCP",
  "VFU",
  "WAP",
  "NMV",
  "WAXIT",
  "LCWA",
  "SA",
  "SFFP",
  "SPPk"
]);
var partyInfoSchema = record(partyCodeSchema, string());
var parties = {
  ALP: "Australian Labor Party",
  AJP: "Animal Justice Party",
  GRN: "Australian Greens",
  IND: "Independent",
  KAP: "Katter's Australian Party",
  LIB: "Liberal Party of Australia",
  LCQP: "Legalise Cannabis Queensland Party",
  LNP: "Liberal National Party",
  LDP: "Libertarian Party",
  LBT: "Libertarian Party",
  ONP: "One Nation Party",
  PHON: "One Nation Party",
  OTH: "Others",
  ACP: "Australian Christian Party",
  ANTI_SOC: "Anti-Socialist Party",
  ASP: "Australian Shooters Party",
  CLP: "Northern Territory Country Liberal Party",
  CP: "Australian Country Party",
  DEM: "Australian Democrats",
  FLP: "Federal Labor Party",
  FSU: "Farmers and Settlers Union",
  FT: "Free Trade",
  FU: "Farmers' Union",
  IND_LAB: "Independent Labor",
  IND_NAT: "Independent Nationalist",
  IND_PROT: "Independent Protectionist",
  LANG_LAB: "Lang Labor Party",
  LCL: "Liberal Country League",
  LCP: "Liberal Country Party",
  LNQ: "Liberal National Party of Queensland",
  LP: "Liberal Party of Australia",
  NAT: "National Party of Australia",
  NAT_FARMERS: "Nationalist and Farmers",
  NCP: "National Country Party",
  NP: "National Party of Australia",
  NPWA: "National Party of Western Australia",
  PROT: "Protectionist Party",
  SAP: "Sustainable Australia Party",
  ST_CP: "State Country Party",
  UAP: "United Australia Party",
  UCP: "United Country Party",
  VFU: "Victorian Farmers' Union",
  WAP: "Western Australia Party",
  NMV: "No Mandatory Vaccination",
  WAXIT: "Small Business Party",
  LCWA: "Legalise Cannabis WA Party",
  SA: "Socialist Alliance",
  SFFP: "Shooters, Fishers and Farmers Party",
  SPPk: "Stop Pedophiles! Protect kiddies!"
};
var areaCodeSchema = enum2([
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
  "Federal"
]);
var predictionBasisSchema = enum2(["Algorithm", "Manual", "None"]);
var predictionCertaintySchema = enum2([
  "InDoubt",
  "TooClose",
  "Probable",
  "Definite"
]);
var partyDtoSchema = object({
  state: string(),
  partyCode: partyCodeSchema.or(string()),
  partyName: string().optional(),
  isCoalition: boolean(),
  swingPct: number().nullable(),
  seatsWon: number(),
  seatsAhead: number(),
  seatsChanged: number(),
  seatsPredicted: number().nullable(),
  seatsLost: number().optional(),
  seatsGained: number().optional(),
  votePct: number(),
  voteCount: number(),
  tpp: number(),
  tppChange: number(),
  tppHistorical: number()
});
var candidatesDtoSchema = object({
  candidateId: number(),
  firstName: string().nullable(),
  lastName: string(),
  partyCode: string(),
  partyName: string().optional(),
  photoUrl: string(),
  seatId: string(),
  isIncumbent: boolean(),
  swingPct: number().nullable(),
  primaryVotesPct: number(),
  primaryVotes: number()
});
var twoPartyPreferredDtoSchema = object({
  seatCode: string(),
  partyCode: string(),
  candidateId: number(),
  candidateFirstName: string().nullable(),
  candidateLastName: string(),
  candidatePartyCode: string().nullable(),
  candidatePartyName: string().optional(),
  incumbent: boolean(),
  primaryVotes: number(),
  changePrimaryPct: number().nullable(),
  changeTcpPct: number().nullable(),
  tcpVotesPctCurrent: number().nullable(),
  winner: boolean()
});
var seatDtoSchema = object({
  seatId: string(),
  seatName: string(),
  seatRegion: string(),
  state: string(),
  postcodes: array(number()).readonly(),
  incumbentName: string().nullable(),
  incumbentParty: string().nullable(),
  incumbentIsCoalition: boolean(),
  incumbentPartyCode: string().optional(),
  predictionBasis: predictionBasisSchema.optional(),
  predictionCertainty: predictionCertaintySchema.optional(),
  predictionParty: string().optional(),
  candidates: array(candidatesDtoSchema),
  twoPartyPreferred: array(twoPartyPreferredDtoSchema),
  swingPct: number().nullable(),
  lastUpdated: string().nullable()
});
var areaDtoSchema = object({
  electionId: number(),
  areaCode: string(),
  areaName: string(),
  parties: array(partyDtoSchema),
  pctCounted: number()
});
var electionConfigSchema = object({
  enabled: boolean(),
  electionApi: object({
    electionId: string(),
    frequency: number()
  }),
  mapApiUrl: string().optional().nullable(),
  seatsToWin: number(),
  seatsTotal: number(),
  barTotalSeats: number(),
  electionTitle: string(),
  electionDate: string(),
  electionType: string(),
  state: string().optional()
});
var isElectionConfig = (obj) => {
  return electionConfigSchema.safeParse(obj).success;
};
var seatsPredictedSchema = record(string(), number());

// libs/election-api-types/src/election-response-types.ts
import {
  array as array2,
  nullable,
  number as number2,
  object as object2,
  string as string2
} from "zod";
var transformedDataSchema = object2({
  areas: array2(areaDtoSchema),
  seats: array2(seatDtoSchema),
  needle: number2().optional(),
  lastModified: nullable(string2().datetime())
});
var electionDataResponseSchema = object2({
  data: transformedDataSchema.optional(),
  config: electionConfigSchema.optional()
});
var electionsElectionDefinition = object2({
  wanElectionId: string2(),
  objectKey: string2()
});
var electionsResponseSchema = array2(electionsElectionDefinition);
export {
  areaCodeSchema,
  areaDtoSchema,
  candidatesDtoSchema,
  electionConfigSchema,
  electionDataResponseSchema,
  electionsElectionDefinition,
  electionsResponseSchema,
  isElectionConfig,
  parties,
  partyCodeSchema,
  partyDtoSchema,
  partyInfoSchema,
  seatDtoSchema,
  transformedDataSchema,
  twoPartyPreferredDtoSchema
};
//# sourceMappingURL=index.js.map
